/* Shared primitives for the SignupWizard step components.
 * Split out from the monolithic /pages/SignupWizard.jsx so each step can
 * be co-located with its presentation logic without re-declaring the
 * field wrapper, base input styles or step metadata. */

import { Label } from "@/components/ui/label";
import { AlertCircle } from "lucide-react";
import { User, Mail, Globe, Phone, ShieldCheck } from "lucide-react";

/* -------- Step config (5 steps) ---------------------------------- */
export const STEPS = [
  { id: 1, key: "identity", Icon: User,        label: "Identity",  eyebrow: "Step 1 of 5", hint: "Who are we welcoming?" },
  { id: 2, key: "account",  Icon: Mail,        label: "Account",   eyebrow: "Step 2 of 5", hint: "Email + account type" },
  { id: 3, key: "location", Icon: Globe,       label: "Residence", eyebrow: "Step 3 of 5", hint: "Where you live" },
  { id: 4, key: "contact",  Icon: Phone,       label: "Contact",   eyebrow: "Step 4 of 5", hint: "Phone + date of birth" },
  { id: 5, key: "review",   Icon: ShieldCheck, label: "Review",    eyebrow: "Step 5 of 5", hint: "One last look, then we're done" },
];

/* Friendly little nudges shown under the step header — rotating
   copy keeps the flow feeling more human and less paperwork-y. */
export const STEP_TAGLINES = {
  1: "Tell us who we're welcoming aboard.",
  2: "How you'll log in once we're set up.",
  3: "Required for compliance — only takes a sec.",
  4: "We'll use this for two-factor auth on withdrawals.",
  5: "Almost there — final check and you're trading.",
};

/* "Encouragement" toast text shown when the user advances between
   steps · little wins keep the funnel moving. */
export const STEP_CHEERS = [
  null,
  "Nice to meet you, ", // appended with first name
  "Got it · keep going",
  "Address locked in",
  "All set — one last review",
];

export const TITLES = [
  { value: "mr",  label: "Mr." },
  { value: "mrs", label: "Mrs." },
  { value: "ms",  label: "Ms." },
  { value: "dr",  label: "Dr." },
];

/* Bigger / friendlier base inputs · h-12 + 14.5px so the user can
   actually SEE what they're typing. */
export const baseInputCls  = "wiz-input h-12 px-4 text-[15px] w-full";
export const baseSelectCls = "wiz-select h-12 w-full px-4 text-[15px]";

/* Form field wrapper · uppercase micro-label, error + hint slots */
export function Field({ label, hint, error, children, testid, required = false }) {
  return (
    <div className="flex flex-col gap-1.5" data-testid={testid ? `${testid}-wrap` : undefined}>
      <Label className="text-[12px] font-semibold uppercase tracking-[0.14em] text-[color:var(--wiz-text-2)] flex items-center gap-1">
        {label}
        {required && <span className="text-[color:var(--brand)] text-[12px] leading-none">*</span>}
      </Label>
      {children}
      {error
        ? (
          <div
            className="text-[12px] text-rose-500 dark:text-rose-300 font-medium flex items-center gap-1.5"
            data-testid={testid ? `${testid}-error` : undefined}
          >
            <AlertCircle size={11} className="shrink-0" />
            {error}
          </div>
        )
        : hint ? <div className="text-[12px] text-[color:var(--wiz-text-3)] leading-relaxed">{hint}</div> : null}
    </div>
  );
}
